import React from "react";
import { useEffect, lazy, Suspense } from "react";

import { Loader2, Users, UserCheck, TrendingUp, Clock, ArrowLeft, Shield, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { SessionRefreshIndicator } from "@/components/SessionRefreshIndicator";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAdminState } from "@/hooks/useAdminState";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import { useUserRole } from "@/hooks/useUserRole";

// Lazy load all admin components for better code splitting
const DataMigrationManager = lazy(() => import("@/components/admin/DataMigrationManager"));
const BoardCreationMonitor = lazy(() => import("@/components/admin/BoardCreationMonitor").then(m => ({ default: m.BoardCreationMonitor })));
const BoardCreationRecovery = lazy(() => import("@/components/admin/BoardCreationRecovery"));
const BoardRecoveryAnalytics = lazy(() => import("@/components/admin/BoardRecoveryAnalytics"));
const RecoveryHistoryTimeline = lazy(() => import("@/components/admin/RecoveryHistoryTimeline"));
const NotificationPreferences = lazy(() => import("@/components/admin/NotificationPreferences").then(m => ({ default: m.NotificationPreferences })));
const PrebuiltBoardsManager = lazy(() => import("@/components/admin/PrebuiltBoardsManager").then(m => ({ default: m.PrebuiltBoardsManager })));
const BoardCreationAnalytics = lazy(() => import("@/components/admin/BoardCreationAnalytics"));
const RateLimitTiersMonitor = lazy(() => import("@/components/admin/RateLimitTiersMonitor").then(m => ({ default: m.RateLimitTiersMonitor })));
const SessionFrameworksManager = lazy(() => import("@/components/admin/SessionFrameworksManager").then(m => ({ default: m.SessionFrameworksManager })));
const WaitlistManagement = lazy(() => import("@/components/admin/WaitlistManagement").then(m => ({ default: m.WaitlistManagement })));
const ConventionPacksManager = lazy(() => import("@/components/admin/ConventionPacksManager").then(m => ({ default: m.ConventionPacksManager })));
const ContentCreationManager = lazy(() => import("@/components/admin/ContentCreationManager").then(m => ({ default: m.ContentCreationManager })));
const ContentGapAnalysis = lazy(() => import("@/components/admin/ContentGapAnalysis").then(m => ({ default: m.ContentGapAnalysis })));
const ContentRecommendationSettings = lazy(() => import("@/components/admin/ContentRecommendationSettings").then(m => ({ default: m.ContentRecommendationSettings })));
const EmailPreviewer = lazy(() => import("@/components/admin/EmailPreviewer"));
const EmailBounceManagement = lazy(() => import("@/components/admin/EmailBounceManagement").then(m => ({ default: m.EmailBounceManagement })));
const TwoFactorManagement = lazy(() => import("@/components/admin/TwoFactorManagement").then(m => ({ default: m.TwoFactorManagement })));
const SecurityAlerts = lazy(() => import("@/components/admin/SecurityAlerts").then(m => ({ default: m.SecurityAlerts })));
const EmailAccessAudit = lazy(() => import("@/components/admin/EmailAccessAudit").then(m => ({ default: m.EmailAccessAudit })));
const UnauthorizedAccessMonitor = lazy(() => import("@/components/admin/UnauthorizedAccessMonitor").then(m => ({ default: m.UnauthorizedAccessMonitor })));
const SessionTerminationsMonitor = lazy(() => import("@/components/admin/SessionTerminationsMonitor").then(m => ({ default: m.SessionTerminationsMonitor })));
const CommunicationCodeMigrationTool = lazy(() => import("@/components/admin/CommunicationCodeMigrationTool").then(m => ({ default: m.CommunicationCodeMigrationTool })));

const AdminPanel = () => {
  const { isAdmin, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  
  // Enable keyboard shortcuts for back navigation
  useKeyboardNavigation({
    onBack: () => navigate("/dashboard"),
  });
  
  // Use centralized admin state management
  const {
    waitlistUsers,
    approvedUsers,
    stats,
    loading,
    activeTab,
    fetchData,
    handleTabChange,
    handleGoToDashboard,
    handleGoToIPManagement,
    handleGoToPromptManagement,
    handleGoToOnboardingAnalytics,
  } = useAdminState();

  useEffect(() => {
    if (!roleLoading && !isAdmin) {
      navigate("/");
      return;
    }
  }, [isAdmin, roleLoading, navigate]);

  const AdminSuspenseFallback = () => (
    <div className="flex items-center justify-center p-8">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );

  if (roleLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={handleGoToDashboard}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Admin Panel</h1>
              <p className="text-muted-foreground">Manage waitlist and user approvals</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <SessionRefreshIndicator />
            <Button onClick={() => fetchData()} variant="outline">
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Waitlisted Users</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalWaitlisted}</div>
              <p className="text-xs text-muted-foreground">
                {stats.unverifiedWaitlisted > 0 && (
                  <span className="text-yellow-600">{stats.unverifiedWaitlisted} unverified</span>
                )}
                {stats.unverifiedWaitlisted === 0 && "All verified"}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Recently Approved</CardTitle>
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.recentlyApproved}</div>
              <p className="text-xs text-muted-foreground">Last 7 days</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Approved</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalApproved}</div>
              <p className="text-xs text-muted-foreground">Active users</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unverified Users</CardTitle>
              <Mail className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{stats.unverifiedCount}</div>
              <p className="text-xs text-muted-foreground">
                {stats.unverifiedApproved > 0 && `${stats.unverifiedApproved} approved`}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Approval Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.approvalRate.toFixed(1)}%</div>
              <p className="text-xs text-muted-foreground">Of total signups</p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 lg:w-auto">
            <TabsTrigger value="users">User Management</TabsTrigger>
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="monitoring">Monitoring</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="system">System</TabsTrigger>
          </TabsList>

          {/* User Management Tab */}
          <TabsContent value="users" className="space-y-6">
            <Suspense fallback={<AdminSuspenseFallback />}>
              <div className="mb-6">
                <NotificationPreferences />
              </div>

              <CommunicationCodeMigrationTool />

              <TwoFactorManagement />

              <SecurityAlerts />

              <EmailAccessAudit />

              <EmailPreviewer />

              <EmailBounceManagement />

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Security & IP Management
                  </CardTitle>
                  <CardDescription>
                    Monitor and manage IP reputation, blocked addresses, and security incidents
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={handleGoToIPManagement} className="w-full sm:w-auto">
                    Open IP Management Dashboard
                  </Button>
                </CardContent>
              </Card>

              <WaitlistManagement 
                waitlistUsers={waitlistUsers}
                approvedUsers={approvedUsers}
              />
            </Suspense>
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content" className="space-y-6">
            <Suspense fallback={<AdminSuspenseFallback />}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Prompt Management System
                  </CardTitle>
                  <CardDescription>
                    Manage prompt templates, versions, and rollouts with A/B testing
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={handleGoToPromptManagement} className="w-full sm:w-auto">
                    Open Prompt Management
                  </Button>
                </CardContent>
              </Card>
              
              <ContentRecommendationSettings />
              <ContentGapAnalysis />
              <ContentCreationManager />
              <PrebuiltBoardsManager />
              <SessionFrameworksManager />
              <ConventionPacksManager />
            </Suspense>
          </TabsContent>

          {/* Monitoring Tab */}
          <TabsContent value="monitoring" className="space-y-6">
            <Suspense fallback={<AdminSuspenseFallback />}>
              <SessionTerminationsMonitor />
              <UnauthorizedAccessMonitor />
              <RateLimitTiersMonitor />
              <BoardCreationMonitor />
              <div className="space-y-6">
                <BoardRecoveryAnalytics />
                <BoardCreationRecovery />
                <RecoveryHistoryTimeline />
              </div>
            </Suspense>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <Suspense fallback={<AdminSuspenseFallback />}>
              <BoardCreationAnalytics />
              
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Onboarding Analytics
                  </CardTitle>
                  <CardDescription>
                    Track user behavior through the onboarding flow and optimize conversion
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={handleGoToOnboardingAnalytics} className="w-full sm:w-auto">
                    View Onboarding Analytics Dashboard
                  </Button>
                </CardContent>
              </Card>
            </Suspense>
          </TabsContent>

          {/* System Tab */}
          <TabsContent value="system" className="space-y-6">
            <Suspense fallback={<AdminSuspenseFallback />}>
              <DataMigrationManager />
            </Suspense>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
